import { Component } from '@angular/core';
import * as moment from 'moment';
import { UserService } from 'src/app/services/user.service';
import { SystemService } from 'src/app/services/system.service';
import { SystemSetting } from 'src/app/shared/models/general/system-setting';
import { SettingsService } from 'src/app/services/settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { JobService } from 'src/app/services/job.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'tools-component',
  templateUrl: './tools.component.html',
  styleUrls: ['./tools.component.scss'],
})
export class ToolsComponent {
  showError: boolean = false;
  errorResponse: HttpErrorResponse;

  showRunJobsTools: boolean = !!environment.showRunJobsTools;

  jobName_Prebooking: string = 'prebooking';
  jobName_ProcessDeals: string = 'processdeals';
  isBusyCheckingStatus: boolean = false;
  isBusyRunningPrebooking: boolean = false;
  isBusyRunningProcessTrades: boolean = false;
  isBusyRunningBankBalances: boolean = false;
  isBusyCheckingProcessDealsStatus: boolean = false;
  lastRun: string = '';
  lastStarted: string = '';
  isJobRunning: boolean = false;
  processDealsLastRun: string = '';
  processDealsLastStarted: string = '';
  isProcessDealsRunning: boolean = false;
  bankBalancesMessage: string = '';

  autoProcessingSetting: SystemSetting = new SystemSetting();

  ngOnInit(): void {
    this.GetAutoProcessingSetting();
  }

  constructor(
    public usersService: UserService,
    public systemService: SystemService,
    public settingsService: SettingsService,
    public jobService: JobService
  ) {
    this.checkJobStatus(this.jobName_Prebooking);
    if (this.showRunJobsTools) {
      this.checkProcessDealsJobStatus();
    }
  }

  toggleAutoProcessing() {
    this.autoProcessingSetting.value = this.isAutoProcessingEnabled ? 'Disabled' : 'Enabled';
    this.UpdateSystemSetting(this.autoProcessingSetting);
  }

  private GetAutoProcessingSetting() {
    this.showError = false;
    this.autoProcessingSetting = null;
    this.systemService.GetSystemSetting('MercantileIntegration', 'DealAPIAutoProcessing').subscribe({
      next: (setting: SystemSetting) => {
        this.autoProcessingSetting = setting;
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
      },
    });
  }

  private UpdateSystemSetting(sysSetting: SystemSetting) {
    this.showError = false;
    this.systemService.UpdateSystemSetting(sysSetting).subscribe({
      next: (setting: SystemSetting) => {
        this.autoProcessingSetting = setting;
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
      },
    });
  }

  // Getter: Translates the string value ('Enabled' or 'Disabled') to a boolean (true or false)
  get isAutoProcessingEnabled(): boolean {
    return this.autoProcessingSetting.value === 'Enabled';
  }

  // Setter: Translates the boolean value (true or false) from the checkbox back to a string
  set isAutoProcessingEnabled(value: boolean) {
    this.autoProcessingSetting.value = value ? 'Enabled' : 'Disabled';
  }

  isItAfterHours(): boolean {
    const now = moment();
    if (now.weekday() === 6 || now.weekday() === 0) {
      return true;
    }

    const currentHour = moment().hour();
    return currentHour < 7 || currentHour >= 18;
  }

  runPrebookingProcessor() {
    if (this.isBusyRunningPrebooking) {
      return;
    }

    this.isBusyRunningPrebooking = true;
    this.jobService.RunPrebookingProcessor().subscribe({
      next: (_) => {
        this.lastStarted = moment().format('Do MMMM h:mm:ss a');
        this.isJobRunning = true;
        this.isBusyRunningPrebooking = false;
        setTimeout(() => {
          this.checkJobStatus(this.jobName_Prebooking);
        }, 30000);
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
        this.isBusyRunningPrebooking = false;
      },
    });
  }

  runProcessTrades() {
    if (this.isBusyRunningProcessTrades) {
      return;
    }

    this.isBusyRunningProcessTrades = true;
    this.jobService.RunProcessDealTransactions().subscribe({
      next: (_) => {
        this.processDealsLastStarted = moment().format('Do MMMM h:mm:ss a');
        this.isProcessDealsRunning = true;
        this.isBusyRunningProcessTrades = false;
        setTimeout(() => {
          this.checkProcessDealsJobStatus();
        }, 30000);
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
        this.isBusyRunningProcessTrades = false;
      },
    });
  }

  runBankBalances() {
    if (this.isBusyRunningBankBalances) {
      return;
    }

    this.isBusyRunningBankBalances = true;
    this.bankBalancesMessage = '';
    this.jobService.RunUpdateAvailableBalance().subscribe({
      next: (_) => {
        this.bankBalancesMessage = `Triggered ${moment().format('Do MMMM h:mm:ss a')}`;
        this.isBusyRunningBankBalances = false;
      },
      error: (error) => {
        this.errorResponse = error;
        this.showError = true;
        this.isBusyRunningBankBalances = false;
      },
    });
  }

  checkJobStatus(jobName: string) {
    if (this.isBusyCheckingStatus) {
      return;
    }

    this.isBusyCheckingStatus = true;
    this.jobService.GetJobStatus(jobName).subscribe({
      next: (jobData) => {
        this.lastRun = moment(jobData.lastCompleted).format('Do MMMM h:mm:ss a');
        this.lastStarted = moment(jobData.lastStarted).format('Do MMMM h:mm:ss a');
        this.isJobRunning = jobData.isRunning;
        this.isBusyCheckingStatus = false;
      },
      error: (error) => {
        console.error(error);
        this.errorResponse = error;
        this.showError = true;
        this.isBusyCheckingStatus = false;
      },
    });
  }

  checkProcessDealsJobStatus() {
    if (this.isBusyCheckingProcessDealsStatus) {
      return;
    }

    this.isBusyCheckingProcessDealsStatus = true;
    this.jobService.GetJobStatus(this.jobName_ProcessDeals).subscribe({
      next: (jobData) => {
        this.processDealsLastRun = moment(jobData.lastCompleted).format('Do MMMM h:mm:ss a');
        this.processDealsLastStarted = moment(jobData.lastStarted).format('Do MMMM h:mm:ss a');
        this.isProcessDealsRunning = jobData.isRunning;
        this.isBusyCheckingProcessDealsStatus = false;
      },
      error: (error) => {
        console.error(error);
        this.errorResponse = error;
        this.showError = true;
        this.isBusyCheckingProcessDealsStatus = false;
      },
    });
  }
}
